import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import GradeBadge from '../components/GradeBadge';
import VideoEmbed from '../../components/VideoEmbed';
import CreatorLink from '../../components/CreatorLink';
import { useSiteData } from '../data/SiteDataContext';
import { resolveMediaUrl, resolveRichTextMedia } from '../../utils/media';
import {
    VIDEOS_PATH,
    getPostCustomThumbnail,
    getPostVideo,
    getVideoChannelUrl,
    getVideoProviderLabel,
    getVideoSourceUrl,
} from '../../utils/video';
import { convertPlainTextToHtml, normalizeRichTextContent, isRichTextEmpty } from '../../utils/richText';
import {
    formatByline,
    formatFullDate,
    formatReadTime,
    getPostDateTimeAttr,
} from '../lib/format';
import { normalizeTagValue } from '../../utils/tagUtils';
import { addVideoFrameHints } from '../../utils/videoFrameHint';
import { applyEmbedFacades } from '../../utils/embedFacades';
import { tagLink } from '../lib/tagIndex';
import SeriesNavigator, { SeriesPartNav } from '../components/SeriesNavigator';

// Wrap images in .grain-wrap spans so in-content images get the animated
// grain layer — same behavior as the live theme's main.js.
const applyGrainWrap = (container) => {
    if (!container) {
        return;
    }
    container.querySelectorAll('img').forEach((img) => {
        if (img.closest('.grain-wrap') || img.closest('.no-grain') || img.classList.contains('no-grain')) {
            return;
        }
        const parent = img.parentElement;
        if (parent && !parent.classList.contains('grain-wrap')) {
            const wrapper = document.createElement('span');
            wrapper.className = 'grain-wrap';
            // Inside figures the wrapper must fill the figure so the image
            // keeps its width: 100% sizing (floats/width presets from the
            // editor); elsewhere keep the original inline-block behavior.
            const isBlockContext = parent.tagName === 'A' || parent.tagName === 'FIGURE';
            wrapper.style.display = isBlockContext ? 'block' : 'inline-block';
            if (parent.tagName === 'FIGURE') {
                wrapper.style.width = '100%';
            }
            parent.insertBefore(wrapper, img);
            wrapper.appendChild(img);
        }
    });
};

function ArticlePage() {
    const { postId } = useParams();
    const {
        getPostCategory,
        getSeriesForPost,
        getAdjacentPosts,
        upsertPost,
    } = useSiteData();

    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);
    const contentRef = useRef(null);
    const furtherRef = useRef(null);

    useEffect(() => {
        let active = true;
        setPost(null);
        setError(null);

        const load = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/posts/${postId}`);
                if (active) {
                    setPost(response.data);
                    // Sync the site-wide index so SEO meta and series data
                    // reflect this post even if the startup index missed it.
                    upsertPost(response.data);
                }
            } catch (requestError) {
                console.error('[ERROR] Failed to load document:', requestError);
                if (active) {
                    setError('Document not found.');
                }
            }
        };

        load();

        return () => {
            active = false;
        };
    }, [postId, upsertPost]);

    // Embeds become click-to-play facades before the browser paints, so an
    // off-screen (lazy) player never starts loading. Runs ahead of the grain
    // pass, which then leaves the facade stills alone.
    useLayoutEffect(() => {
        if (post) {
            applyEmbedFacades(contentRef.current);
            applyEmbedFacades(furtherRef.current);
        }
    }, [post]);

    useEffect(() => {
        if (post) {
            applyGrainWrap(contentRef.current);
        }
    }, [post]);

    if (error) {
        return (
            <p className="small-meta" style={{ padding: '2rem 0' }}>
                {error} <Link to="/" style={{ textDecoration: 'underline' }}>Return home</Link>
            </p>
        );
    }

    if (!post) {
        return <p className="small-meta site-loading">Loading …</p>;
    }

    const category = getPostCategory(post);
    const series = getSeriesForPost(post);
    const { previous, next } = getAdjacentPosts(post._id);
    // Condoned Media: the player takes the featured-image slot and the
    // commentary follows below it.
    const video = getPostVideo(post);
    const heroSrc = video ? '' : resolveMediaUrl(post.thumbnail);
    const videoSourceUrl = getVideoSourceUrl(video);
    const videoChannelUrl = getVideoChannelUrl(video);
    // Frame hints go into the HTML itself, so a poster-less video starts
    // loading with its final src instead of being re-pointed after paint.
    // Empty content falls back to the description, which is plain text (and
    // not sanitized server-side): it is always escaped, even when it looks
    // like HTML.
    const contentHtml = post.content && !isRichTextEmpty(post.content)
        ? addVideoFrameHints(resolveRichTextMedia(normalizeRichTextContent(post.content)))
        : convertPlainTextToHtml(post.description);
    const hasBibliography = !isRichTextEmpty(post.bibliography);

    return (
        <article className="single-article">
            <SeriesNavigator series={series} postId={post._id} />

            <header className="article-header">
                <div className="article-header-meta">
                    {video && (
                        <Link className="eyebrow" to={VIDEOS_PATH}>Condoned Media</Link>
                    )}
                    {category && (
                        <Link className="eyebrow" to={`/category/${category.id}`}>
                            {category.label}
                        </Link>
                    )}
                    <GradeBadge rating={post.confidenceScoreRating} />
                </div>
                <h1 className="article-title">{post.title}</h1>
                <div className="article-info-bar">
                    <span className="article-byline small-meta">{formatByline(post)}</span>
                    <span className="sep small-meta">·</span>
                    <time className="article-date small-meta" dateTime={getPostDateTimeAttr(post)}>
                        {formatFullDate(post)}
                    </time>
                    <span className="sep small-meta">·</span>
                    <span className="article-read-time small-meta">{formatReadTime(post)}</span>
                </div>
            </header>

            {video && (
                <figure className="article-video">
                    <VideoEmbed
                        video={video}
                        title={post.title}
                        poster={getPostCustomThumbnail(post)}
                    />
                    <figcaption className="article-video-caption">
                        {video.channel && (
                            <span className="article-video-channel">
                                Video by{' '}
                                {videoChannelUrl ? (
                                    <CreatorLink href={videoChannelUrl}>{video.channel}</CreatorLink>
                                ) : video.channel}
                            </span>
                        )}
                        {video.channel && videoSourceUrl && <span className="sep" aria-hidden="true">·</span>}
                        {videoSourceUrl && (
                            <CreatorLink className="article-video-source" href={videoSourceUrl}>
                                Watch on {getVideoProviderLabel(video)} ↗
                            </CreatorLink>
                        )}
                    </figcaption>
                </figure>
            )}

            {heroSrc && (
                <figure className="article-featured-image grain-wrap">
                    <img src={heroSrc} alt={post.title} loading="eager" />
                </figure>
            )}

            <div
                className="article-content"
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: contentHtml }}
            />

            {hasBibliography && (
                <section className="further-section" aria-label="Further Reading and Watching">
                    <div className="further-section-title">Further Reading &amp; Watching</div>
                    <div
                        className="further-content"
                        ref={furtherRef}
                        dangerouslySetInnerHTML={{ __html: addVideoFrameHints(resolveRichTextMedia(normalizeRichTextContent(post.bibliography))) }}
                    />
                </section>
            )}

            {Array.isArray(post.tags) && post.tags.length > 0 && (
                <div className="article-tags">
                    <span className="article-tags-label">Filed under</span>
                    <div className="tag-cloud">
                        {post.tags.map((tag) => {
                            const value = normalizeTagValue(tag);
                            if (!value) {
                                return null;
                            }
                            return (
                                <Link key={value} className="ds-tag-chip" to={tagLink(value)}>
                                    {tag}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            <SeriesPartNav series={series} postId={post._id} />

            {(previous || next) && (
                <nav className="navigation post-navigation" aria-label="Posts">
                    <h2 className="screen-reader-text">Post navigation</h2>
                    <div className="nav-links">
                        <div className="nav-previous">
                            {previous && (
                                <Link rel="prev" to={`/posts/${previous._id}`}>
                                    <span className="eyebrow">← Previous</span>
                                    <span className="nav-title">{previous.title}</span>
                                </Link>
                            )}
                        </div>
                        <div className="nav-next">
                            {next && (
                                <Link rel="next" to={`/posts/${next._id}`}>
                                    <span className="eyebrow">Next →</span>
                                    <span className="nav-title">{next.title}</span>
                                </Link>
                            )}
                        </div>
                    </div>
                </nav>
            )}
        </article>
    );
}

export default ArticlePage;
